/**
 * Pure helpers for classifying token-refresh failures.
 * Kept out of AuthProvider so network / revoked / expired branches stay unit-testable.
 */

/** Why a session ended — drives the user-facing sign-out message. */
export type SessionEndReason = "revoked_by_other_login" | "expired" | "payment_overdue" | "generic"

type RefreshMutationResult = {
  data?: { refreshAccessToken?: { jwt?: string | null } | null } | null
  errors?: ReadonlyArray<{ message?: string }> | null
}

/** Extracts a lowercased message from an unknown (possibly Apollo) error for classification. */
export function extractRefreshErrorMessage(err: unknown): string {
  if (!err || typeof err !== "object") {
    return typeof err === "string" ? err.toLowerCase() : ""
  }
  const apolloError = err as { message?: string; graphQLErrors?: Array<{ message?: string }> }
  const graphQLMessage = apolloError.graphQLErrors?.map(e => e?.message ?? "").join(" ") ?? ""
  return `${graphQLMessage} ${apolloError.message ?? ""}`.toLowerCase()
}

/** Maps a definitive (server-side) refresh rejection to a session-end reason. */
export function classifyUnrecoverableRefresh(err: unknown): SessionEndReason {
  const message = extractRefreshErrorMessage(err)
  // The CMS throws "Invalid refresh token" when the cookie's token is absent from the DB —
  // for a single-session app that means a newer login on another device rotated it out.
  if (message.includes("invalid refresh token")) {
    return "revoked_by_other_login"
  }
  // PAYMENT_OVERDUE_REFRESH_ERROR in the CMS (`helpers/auth/paymentOverdue.ts`).
  if (message.includes("outstanding payments")) {
    return "payment_overdue"
  }
  if (message.includes("expired") || message.includes("refresh token is required")) {
    return "expired"
  }
  return "generic"
}

/**
 * Classifies a failed refresh call. A definitive GraphQL error from the server means the
 * refresh token itself was rejected (invalid/expired/revoked) and the user must log in again.
 * Network errors, offline state, or transport failures are transient and must NOT wipe the session.
 *
 * @param isOnline - Override for tests; defaults to `navigator.onLine` in the browser.
 */
export function isRecoverableRefreshError(
  err: unknown,
  isOnline: boolean = typeof navigator !== "undefined" ? navigator.onLine : true
): boolean {
  if (!isOnline) {
    return true
  }
  if (!err || typeof err !== "object") {
    // Nothing we can classify → treat as recoverable to avoid a false logout.
    return true
  }
  const apolloError = err as { graphQLErrors?: unknown[]; networkError?: unknown }
  if (Array.isArray(apolloError.graphQLErrors) && apolloError.graphQLErrors.length > 0) {
    // Server processed the request and rejected the refresh token.
    return false
  }
  // Only a transport-level failure (or nothing classifiable) → transient.
  return true
}

/** GraphQL refresh failures are returned in `errors` (errorPolicy: "all") without throwing. */
export function getRefreshMutationFailure(
  result: RefreshMutationResult
): { outcome: "unrecoverable"; endReason: SessionEndReason } | null {
  if (result.data?.refreshAccessToken?.jwt) {
    return null
  }
  if (result.errors?.length) {
    return { outcome: "unrecoverable", endReason: classifyUnrecoverableRefresh({ graphQLErrors: result.errors }) }
  }
  return { outcome: "unrecoverable", endReason: "generic" }
}
